import type { Filters, Jobs, Job, JobsStatistics } from '../../reducers/jobs/jobs'
import type { ProductUpgrade } from '../../reducers/entitlement/entitlement'

export const FETCH: 'jobs/FETCH' = 'jobs/FETCH'
export const SET: 'jobs/SET' = 'jobs/SET'
export const REFETCH: 'jobs/REFETCH' = 'jobs/REFETCH'
export const REFETCHED: 'jobs/REFETCHED' = 'jobs/REFETCHED'
export const RESUME: 'jobs/RESUME' = 'jobs/RESUME'
export const SUSPEND: 'jobs/SUSPEND' = 'jobs/SUSPEND'
export const FETCH_STATISTICS: 'jobs/FETCH_STATISTICS' = 'jobs/FETCH_STATISTICS'
export const FETCH_STATISTICS_FAILED: 'jobs/FETCH_STATISTICS_FAILED' = 'jobs/FETCH_STATISTICS_FAILED'
export const STORE_FETCHED_STATISTICS: 'jobs/STORE_FETCHED_STATISTICS' = 'jobs/STORE_FETCHED_STATISTICS'
export const REQUEST_UPGRADE: 'jobs/REQUEST_UPGRADE' = 'jobs/REQUEST_UPGRADE'
export const UPGRADE_REQUESTED: 'jobs/UPGRADE_REQUESTED' = 'jobs/UPGRADE_REQUESTED'
export const UPGRADE_FAILED: 'jobs/UPGRADE_FAILED' = 'jobs/UPGRADE_FAILED'

export type FetchJobsAction = {
  type: typeof FETCH
  payload: undefined
}

export type RefetchJobAction = {
  type: typeof REFETCH
  payload: string
}

export type JobRefetchedAction = {
  type: typeof REFETCHED
  payload: Job
}

export type SetJobsAction = {
  payload: {
    jobs: Jobs
    filterAmounts: Filters
    totalPageCount?: number
  },
  type: typeof SET
}

export type ResumeJobAction = {
  payload: string
  type: typeof RESUME
}

export type SuspendJobAction = {
  payload: string
  type: typeof SUSPEND
}

export type FetchJobsStatisticsAction = {
  payload: Array<string>
  type: typeof FETCH_STATISTICS
}

export type FetchJobsStatisticsFailedAction = {
  payload?: string
  type: typeof FETCH_STATISTICS_FAILED
}

export type StoreFetchedJobsStatisticsAction = {
  payload: JobsStatistics
  type: typeof STORE_FETCHED_STATISTICS
}

export type RequestJobUpgradeAction = {
  type: typeof REQUEST_UPGRADE
  payload: {
    jobId: string
    upgrade: ProductUpgrade
    playhead: number
  }
}

export type JobUpgradeRequestedAction = {
  type: typeof UPGRADE_REQUESTED
  payload: {
    jobId: string
    upgrade: ProductUpgrade
    playhead: number
  }
}

export type JobUpgradeFailedAction = {
  type: typeof UPGRADE_FAILED
  payload: {
    jobId: string
    upgrade: ProductUpgrade
    playhead: number
  }
}

export type JobsActions =
  | FetchJobsAction
  | FetchJobsStatisticsAction
  | FetchJobsStatisticsFailedAction
  | JobRefetchedAction
  | JobUpgradeFailedAction
  | JobUpgradeRequestedAction
  | RefetchJobAction
  | RequestJobUpgradeAction
  | ResumeJobAction
  | SetJobsAction
  | StoreFetchedJobsStatisticsAction
  | SuspendJobAction

export const fetchJobs = (): FetchJobsAction => ({
  type: FETCH,
  payload: undefined,
})

export const setJobs = (
  jobs: SetJobsAction['payload'],
): SetJobsAction => ({
  payload: jobs,
  type: SET,
})

export const resumeJob = (id: ResumeJobAction['payload']): ResumeJobAction => ({
  payload: id,
  type: RESUME,
})

export const suspendJob = (id: SuspendJobAction['payload']): SuspendJobAction => ({
  payload: id,
  type: SUSPEND,
})

export const fetchJobsStatistics = (jobIds: FetchJobsStatisticsAction['payload']): FetchJobsStatisticsAction => ({
  payload: jobIds,
  type: FETCH_STATISTICS,
})

export const storeFetchedJobsStatistics = (
  statistics: StoreFetchedJobsStatisticsAction['payload'],
): StoreFetchedJobsStatisticsAction => ({
  payload: statistics,
  type: STORE_FETCHED_STATISTICS,
})

export const fetchJobsStatisticsFailed = (error: string): FetchJobsStatisticsFailedAction => ({
  payload: error,
  type: FETCH_STATISTICS_FAILED,
})

export const requestJobUpgrade = (
  jobId: string,
  upgrade: ProductUpgrade,
  playhead: number,
): RequestJobUpgradeAction => ({
  payload: {
    jobId,
    upgrade,
    playhead,
  },
  type: REQUEST_UPGRADE,
})

export const jobUpgradeRequested = (
  jobId: string,
  upgrade: ProductUpgrade,
  playhead: number,
): JobUpgradeRequestedAction => ({
  payload: {
    jobId,
    upgrade,
    playhead,
  },
  type: UPGRADE_REQUESTED,
})

export const jobUpgradeFailed = (jobId: string, upgrade: ProductUpgrade, playhead: number): JobUpgradeFailedAction => ({
  payload: {
    jobId,
    upgrade,
    playhead,
  },
  type: UPGRADE_FAILED,
})

export const refetchJob = (jobId: string): RefetchJobAction => ({
  payload: jobId,
  type: REFETCH,
})

export const jobRefetched = (job: Job): JobRefetchedAction => ({
  payload: job,
  type: REFETCHED,
})
